import React, { useRef, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import NavBar from '../components/NavBar';
import LessonPlanEditor from '../components/LessonPlanEditor';
import LessonPlanDisplaySidebar from '../components/LessonPlanDisplaySidebar';
import LessonPlanAIChat from '../components/LessonPlanAIChat';
import ProtectedRoute from '../components/ProtectedRoute';
import { useFeedback } from '../contexts/FeedbackContext';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../utils/translations';
import { track } from '../utils/analytics';
import { invalidateDashboardPlans } from '../utils/dashboardInvalidation';
import '../App.css';
import styles from './DisplayLessonPlan.module.css';
import { generatePDF } from '../utils/pdfGenerator';

function DisplayLessonPlanContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const lessonIdFromQuery = searchParams.get('id');
  const data = location.state || {};
  const { openFeedbackPanel } = useFeedback();
  const { language: uiLanguage } = useLanguage();

  const handleFeedback = () => {
    const lessonId = lessonIdFromQuery || data.id;
    openFeedbackPanel('lesson_plan', {
      page_id: 'display-lesson-plan',
      page_name: 'Display Lesson Plan',
      lesson_id: lessonId || null,
      page_elements: [
        'Lesson plan editor',
        'Section navigation',
        'AI chat',
        'Save button',
      ],
    });
  };
  const [grade, setGrade] = useState(data.originalSelections?.grade || '');
  const [standard, setStandard] = useState(data.originalSelections?.standard || '');
  const [concept, setConcept] = useState(data.originalSelections?.concept || '');
  const [subconcept, setSubconcept] = useState(data.originalSelections?.subconcept || '');
  const [subject, setSubject] = useState(
    data.originalSelections?.subject || data.originalSelections?.selectedSubject || data.subject || ''
  );
  const [standardDescription, setStandardDescription] = useState(data.originalSelections?.standardDescription || '');
  const [language, setLanguage] = useState(data.originalSelections?.language || '');
  const [moku, setMoku] = useState(data.originalSelections?.moku || '');
  const [island, setIsland] = useState(data.originalSelections?.island || '');

  // Helper function to display language name
  const getLanguageDisplayName = (langCode) => {
    if (langCode === 'en') return 'English';
    if (langCode === 'haw' || langCode === 'hawaiian') return 'ʻŌlelo Hawaiʻi';
    return langCode; // Fallback to code if unknown
  };

  const [selectedField, setSelectedField] = useState(null);
  const [selectedSectionForChat, setSelectedSectionForChat] = useState(null);
  const [showAIChat, setShowAIChat] = useState(true);
  const [pendingPrompt, setPendingPrompt] = useState({ text: '', key: 0 });
  const [quickChatInput, setQuickChatInput] = useState('');

  const SAMPLE_PROMPTS = [
    'Can you explain the cultural elements used?',
    'How is the standard connected to the cultural elements?',
    'Help me in adding two more questions to the worksheet',
  ];
  const [markdown, setMarkdown] = useState(() => {
    // Normalize initial lesson plan data if provided
    if (data.lessonPlan) {
      const normalizeLessonPlan = (data) => {
        if (!data) return '';
        if (typeof data === 'string') {
          let cleaned = data.trim();
          if (cleaned.startsWith('```')) {
            cleaned = cleaned.replace(/^```(?:markdown)?\s*\n?/i, '');
            cleaned = cleaned.replace(/\n?```\s*$/i, '');
          }
          return cleaned;
        }
        return data;
      };
      return normalizeLessonPlan(data.lessonPlan);
    }
    return '';
  });
  const [loading, setLoading] = useState(false);
  const [lessonTitle, setLessonTitle] = useState(data.title || 'Untitled Lesson Plan');
  const [saving, setSaving] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [tempTitle, setTempTitle] = useState(data.title || 'Untitled Lesson Plan');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const editorRef = useRef(null);

  useEffect(() => {
    const id = lessonIdFromQuery || data.id;
    if (id) track('Viewed Display Lesson Plan', { lesson_plan_id: id, screen_name: 'Display Lesson Plan' });
  }, [lessonIdFromQuery, data.id]);

  // Function to normalize lesson plan data (handles both markdown and legacy JSON formats)
  const normalizeLessonPlan = (data) => {
    // Handle null/undefined data
    if (!data) {
      return 'No lesson plan data available';
    }

    // If it's already a string (markdown), return it directly
    if (typeof data === 'string') {
      // Remove any markdown code fences if present
      let cleaned = data.trim();
      if (cleaned.startsWith('```')) {
        cleaned = cleaned.replace(/^```(?:markdown)?\s*\n?/i, '');
        cleaned = cleaned.replace(/\n?```\s*$/i, '');
      }
      return cleaned;
    }
    
    // Legacy JSON format support (for backward compatibility)
    if (typeof data === 'object' && data !== null) {
      const markdownParts = [];
      
      // Add title
      if (data["Lesson Plan Title"]) {
        markdownParts.push(`# ${data["Lesson Plan Title"]}\n`);
      }
      
      // Add lesson overview
      if (data["Lesson Overview"]) {
        markdownParts.push(`## Lesson Overview\n${data["Lesson Overview"]}\n`);
      }
      
      // Add learning objectives
      if (data["Learning Objectives"]) {
        markdownParts.push("## Learning Objectives");
        if (Array.isArray(data["Learning Objectives"])) {
          data["Learning Objectives"].forEach(obj => {
            markdownParts.push(`- ${obj}`);
          });
        } else {
          markdownParts.push(data["Learning Objectives"]);
        }
        markdownParts.push("");
      }
      
      // Add Hawaiian Cultural Connection
      if (data["Hawaiian Cultural Connection"]) {
        markdownParts.push(`## Hawaiian Cultural Connection\n${data["Hawaiian Cultural Connection"]}\n`);
      }
      
      // Add lesson procedure
      if (data["Lesson Procedure"]) {
        markdownParts.push("## Lesson Procedure");
        const procedure = data["Lesson Procedure"];
        
        if (typeof procedure === 'object' && procedure !== null) {
          Object.keys(procedure).forEach(key => {
            if (key === "Introduction" && Array.isArray(procedure[key])) {
              markdownParts.push(`### ${key}`);
              procedure[key].forEach(item => {
                markdownParts.push(`- ${item}`);
              });
              markdownParts.push("");
            } else if (key === "Main Activity" && typeof procedure[key] === 'object') {
              const mainActivity = procedure[key];
              markdownParts.push(`### ${mainActivity.title || key}`);
              if (mainActivity.phases && Array.isArray(mainActivity.phases)) {
                mainActivity.phases.forEach(phase => {
                  if (phase.phase_title) {
                    markdownParts.push(`#### ${phase.phase_title}`);
                  }
                  if (phase.description) {
                    markdownParts.push(phase.description);
                  }
                  markdownParts.push("");
                });
              }
            } else if (key === "Reflection & Discussion" && Array.isArray(procedure[key])) {
              markdownParts.push(`### ${key}`);
              procedure[key].forEach(item => {
                markdownParts.push(`- ${item}`);
              });
              markdownParts.push("");
            }
          });
        }
        markdownParts.push("");
      }
      
      // Assessment generation is disabled for now.
      // Legacy JSON payloads may still include Assessment, but we intentionally
      // skip rendering it in the normalized markdown output.
      
      // Add worksheet (support both new and legacy key variants)
      const worksheetKey = Object.keys(data).find(
        key => key?.trim().toLowerCase().startsWith("worksheet")
      );
      const worksheetData = data["Worksheet"] || (worksheetKey ? data[worksheetKey] : null);
      if (worksheetData) {
        markdownParts.push("## Worksheet");
        const worksheet = worksheetData;
        if (typeof worksheet === 'object' && worksheet !== null) {
          if (worksheet.title) {
            markdownParts.push(`### ${worksheet.title}`);
          }
          if (worksheet.instructions) {
            markdownParts.push(worksheet.instructions);
          }
          if (worksheet.sections && Array.isArray(worksheet.sections)) {
            worksheet.sections.forEach(section => {
              if (section.header) {
                markdownParts.push(`#### ${section.header}`);
              }
              if (section.content && Array.isArray(section.content)) {
                section.content.forEach(item => {
                  markdownParts.push(item);
                });
              }
              markdownParts.push("");
            });
          }
        }
        markdownParts.push("");
      }
      
      // Add reflection questions
      if (data["Reflection Questions"]) {
        markdownParts.push("## Reflection Questions");
        if (Array.isArray(data["Reflection Questions"])) {
          data["Reflection Questions"].forEach((question, index) => {
            markdownParts.push(`${index + 1}. ${question}`);
          });
        } else {
          markdownParts.push(data["Reflection Questions"]);
        }
        markdownParts.push("");
      }
      
      // Add extensions
      if (data["Extensions"]) {
        markdownParts.push("## Extensions");
        if (Array.isArray(data["Extensions"])) {
          data["Extensions"].forEach(item => {
            markdownParts.push(`- ${item}`);
          });
        } else {
          markdownParts.push(data["Extensions"]);
        }
        markdownParts.push("");
      }
      
      // Add conclusion
      if (data["Conclusion"]) {
        markdownParts.push(`## Conclusion\n${data["Conclusion"]}\n`);
      }
      
      return markdownParts.join("\n");
    }
    
    // Fallback: return data as-is
    return data;
  };

  // Fetch lesson plan data if we have an ID (prefer query param)
  useEffect(() => {
    const idToFetch = lessonIdFromQuery || data.id;
    if (idToFetch && !data.lessonPlan) {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      fetch(`/api/lesson-plan/${idToFetch}/`, {
        headers: {
          ...(token && { 'Authorization': `Token ${token}` }),
        },
      })
        .then(response => response.json())
        .then(lessonData => {
          console.log("Fetched lesson plan data:", lessonData);
          // Normalize the lesson plan data (should be markdown, but handle legacy JSON if needed)
          const normalizedMarkdown = normalizeLessonPlan(lessonData.lesson_plan);
          setMarkdown(normalizedMarkdown);
          // Populate header fields when opened via query param
          if (lessonData.title) setLessonTitle(lessonData.title);
          if (lessonData.grade) setGrade(lessonData.grade);
          if (lessonData.standard) setStandard(lessonData.standard);
          if (lessonData.subject) setSubject(lessonData.subject);
          // Handle both snake_case and camelCase field names
          if (lessonData.concept) setConcept(lessonData.concept);
          if (lessonData.subconcept) setSubconcept(lessonData.subconcept);
          if (lessonData.standard_description || lessonData.standardDescription) {
            setStandardDescription(lessonData.standard_description || lessonData.standardDescription);
          }
          if (lessonData.language) setLanguage(lessonData.language);
          if (lessonData.moku) setMoku(lessonData.moku);
          if (lessonData.island) setIsland(lessonData.island);
          setLoading(false);
        })
        .catch(error => {
          console.error('Error fetching lesson plan:', error);
          setLoading(false);
        });
    }
  }, [lessonIdFromQuery, data.id, data.lessonPlan]);

  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showSaveModal) {
        handleSaveModalCancel();
      }
    };

    if (showSaveModal) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [showSaveModal]);

  const handleDownloadPDF = async () => {
    try {
      // Save all editor changes first
      if (editorRef.current?.saveAll) {
        await editorRef.current.saveAll();
      }

      // Get updated markdown
      let updatedMarkdown = markdown;
      if (editorRef.current?.getMarkdown) {
        updatedMarkdown = editorRef.current.getMarkdown();
      }

      // Small delay to ensure editor is saved
      await new Promise(resolve => setTimeout(resolve, 400));

      // Assessment export is disabled for now; export lesson plan only.
      await generatePDF({
        lessonPlan: updatedMarkdown,
        title: lessonTitle,
        grade,
        standard,
        concept,
        standardDescription,
        language
      });
    } catch (error) {
      console.error('Error generating PDF:', error);
      setErrorMessage('Failed to generate PDF. Please try again.');
      setShowErrorModal(true);
    }
  };

  const handleSaveToDashboard = async () => {
    const idToUse = lessonIdFromQuery || data.id;
    track('Clicked Lesson Saved', { lesson_plan_id: idToUse, screen_name: 'Display Lesson Plan' });
    // If lesson plan already has an ID and a title, save directly without modal
    if (idToUse && lessonTitle && lessonTitle !== 'Untitled Lesson Plan') {
      await handleSaveModalConfirm(true);
      return;
    }
    
    // Otherwise show the modal for first-time save
    const defaultTitle = lessonTitle === 'Untitled Lesson Plan' 
      ? `${data.originalSelections?.grade || 'Grade'} ${data.originalSelections?.standard || 'Lesson Plan'}`
      : lessonTitle;
    setTempTitle(defaultTitle);
    setShowSaveModal(true);
  };

  const handleSaveModalConfirm = async (skipModal = false) => {
    try {
      setSaving(true);
      const token = localStorage.getItem('authToken');
      
      // Get updated markdown from editor
      let updatedMarkdown = markdown;
      if (editorRef.current?.getMarkdown) {
        updatedMarkdown = editorRef.current.getMarkdown();
        setMarkdown(updatedMarkdown);
      }
      
      // Use current lessonTitle if skipping modal, otherwise use tempTitle
      const titleToUse = skipModal ? lessonTitle : tempTitle;
      
      console.log("Saving lesson plan to dashboard:", {
        id: lessonIdFromQuery || data.id,
        title: titleToUse,
        skipModal,
        fullData: data
      });
      
      if (!lessonIdFromQuery && !data.id) {
        throw new Error('Lesson plan ID is missing. Cannot save lesson plan.');
      }
      
      // First save the lesson plan content
      const saveContentResponse = await fetch('/api/save-updated-lesson-plan/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`,
        },
        body: JSON.stringify({
          id: lessonIdFromQuery || data.id,
          lesson_plan: updatedMarkdown,
          concept: concept,
          standard_description: standardDescription
        }),
      });

      if (!saveContentResponse.ok) {
        const errorData = await saveContentResponse.json();
        throw new Error(`Failed to save lesson plan content: ${errorData.error || 'Unknown error'}`);
      }
      
      // Then mark as complete with title
      const response = await fetch('/api/lesson-plans/complete/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`,
        },
        body: JSON.stringify({
          id: lessonIdFromQuery || data.id,
          title: titleToUse,
          concept: concept,
          standard_description: standardDescription
        }),
      });

      if (response.ok) {
        track('Lesson Saved Successfully', { lesson_plan_id: lessonIdFromQuery || data.id, title: titleToUse });
        invalidateDashboardPlans(token);
        setLessonTitle(titleToUse);
        setShowSaveModal(false);
        // Show success modal briefly then navigate
        setShowSuccessModal(true);
        
        // Navigate to view-lesson-plan after showing success message
        setTimeout(() => {
          setShowSuccessModal(false);
          navigate('/view-lesson-plan', {
            state: {
              ...data,
              id: lessonIdFromQuery || data.id,
              lessonPlan: updatedMarkdown,
              title: titleToUse,
              originalSelections: {
                grade,
                subject,
                concept,
                subconcept,
                standard: standard,
                standardDescription,
                language,
                moku,
                island,
                files: data.originalSelections?.files,
                fileTypes: data.originalSelections?.fileTypes,
                selectedElements: data.originalSelections?.selectedElements,
              },
            },
          });
        }, 1500);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('API Error Response:', errorData);
        const message = errorData.error || 'Failed to save lesson plan. Please try again.';
        setErrorMessage(message);
        setShowErrorModal(true);
        setSaving(false);
        return;
      }
    } catch (error) {
      console.error('Error saving lesson plan:', error);
      setErrorMessage(error.message || 'Failed to save lesson plan. Please try again.');
      setShowErrorModal(true);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveModalCancel = () => {
    setShowSaveModal(false);
    setTempTitle(lessonTitle);
  };

  const handleModalKeyDown = (e) => {
    if (e.key === 'Enter' && !saving && tempTitle.trim()) {
      handleSaveModalConfirm(false);
    } else if (e.key === 'Escape') {
      handleSaveModalCancel();
    }
  };

  console.log("Markdown passed to DisplayLessonPlan:", data.lessonPlan);

  const getSubjectDisplay = (subjectCode) => {
    if (!subjectCode) return '';
    const normalized = String(subjectCode).toLowerCase();
    if (normalized === 'cs') return 'COMPUTER SCIENCE';
    if (normalized === 'math') return 'MATH';
    if (normalized === 'science') return 'SCIENCE';
    return String(subjectCode).toUpperCase();
  };

  const inferredSubject = (() => {
    if (subject) return subject;
    const c = (concept || '').toLowerCase();
    if (c.includes('math')) return 'math';
    if (c.includes('science')) return 'science';
    if (c.includes('computer') || c.includes('cs')) return 'cs';
    return '';
  })();

  const displayTitle = lessonTitle?.trim() || `${grade || 'Grade'} ${standard || 'Lesson Plan'}`;
  const standardLine = standardDescription
    ? `${standard || 'N/A'}: ${standardDescription}`
    : (standard || 'N/A');

  if (loading) {
    return (
      <div className={styles.container}>
        <NavBar />
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <div>Loading lesson plan...</div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <NavBar />
      <div className={styles.mainLayout}>
        <div className={styles.sidebar}>
          <LessonPlanDisplaySidebar
            data={markdown} 
            onFieldClick={setSelectedField} 
            selectedField={selectedField}
            currentView="full"
            lessonData={{
              id: lessonIdFromQuery || data.id,
              title: lessonTitle,
              lessonPlan: markdown,
              originalSelections: {
                grade,
                subject,
                concept,
                subconcept,
                standard,
                standardDescription,
                language,
                moku,
                island,
                files: data.originalSelections?.files,
                fileTypes: data.originalSelections?.fileTypes,
                selectedElements: data.originalSelections?.selectedElements,
              }
            }}
          />
        </div>
        <div className={`${styles.editorWrapper} ${!showAIChat ? styles.editorWrapperFixed : ''}`}>
          <div id="lesson-plan-content">
            <div className={styles.lessonMetaHeader}>
              <div className={styles.lessonMetaTags}>
                {grade && <span className={styles.metaTag}>{grade}</span>}
                {language && <span className={styles.metaTag}>{getLanguageDisplayName(language).toUpperCase()}</span>}
                {inferredSubject && <span className={styles.metaTag}>{getSubjectDisplay(inferredSubject)}</span>}
              </div>
              <div className={styles.lessonMetaBody}>
                <img
                  src="/assets/KumuConnectLogos/Logo_GreenonLight.png"
                  alt="Kumu Connect"
                  className={styles.lessonMetaLogo}
                />
                <div className={styles.lessonMetaText}>
                  <h1 className={styles.lessonTitleCompact}>{displayTitle}</h1>
                  <p className={styles.lessonConceptCompact}>{concept || getSubjectDisplay(inferredSubject) || 'Lesson'}</p>
                  <p className={styles.lessonStandardCompact}>{standardLine}</p>
                </div>
              </div>
            </div>
            <LessonPlanEditor 
              ref={editorRef}
              data={{ lessonPlan: markdown }} 
              selectedField={selectedField}
              onSectionSelect={setSelectedSectionForChat}
              selectedSectionForChat={selectedSectionForChat}
            />
          </div>
        </div>
        {showAIChat ? (
          <div id="lesson-kokua-chat" className={styles.rightSidebar}>
            <div className={styles.chatHeader}>
              <span className={styles.chatHeaderTitle}>Kōkua Chat</span>
              <button
                className={styles.chatCloseButton}
                onClick={() => setShowAIChat(false)}
                title="Close chat"
              >
                ✕
              </button>
            </div>
            <LessonPlanAIChat
              lessonPlanId={lessonIdFromQuery || data.id}
              selectedSection={selectedSectionForChat}
              onSectionDeselect={() => setSelectedSectionForChat(null)}
              lessonPlanContent={markdown}
              onChangesApplied={(updatedLessonPlan) => {
                setMarkdown(updatedLessonPlan);
              }}
              initialPrompt={pendingPrompt}
            />
          </div>
        ) : (
          <div id="lesson-kokua-chat" className={styles.kokuaChatCard}>
            <div className={styles.kokuaChatCardHeader}>
              <img src="/assets/Solid/Interface/AI.svg" alt="" className={styles.kokuaChatCardIcon} aria-hidden />
              <h3 className={styles.kokuaChatCardTitle}>Kōkua Chat</h3>
            </div>
            <p className={styles.kokuaChatCardText}>
              Ask Kumu Connect&apos;s AI helper to make changes to the whole lesson, or select a section to make specific changes.
            </p>
            <div
              className={styles.kokuaChatInputShell}
              role="button"
              tabIndex={0}
              onClick={() => {
                setPendingPrompt(prev => ({ text: quickChatInput || '', key: prev.key + 1 }));
                setShowAIChat(true);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setPendingPrompt(prev => ({ text: quickChatInput || '', key: prev.key + 1 }));
                  setShowAIChat(true);
                }
              }}
            >
              <input
                type="text"
                value={quickChatInput}
                onChange={(e) => setQuickChatInput(e.target.value)}
                placeholder="Make this lesson more..."
                className={styles.kokuaChatInput}
                onFocus={() => {
                  setPendingPrompt(prev => ({ text: quickChatInput || '', key: prev.key + 1 }));
                  setShowAIChat(true);
                }}
              />
            </div>
            <div className={styles.samplePrompts}>
              {SAMPLE_PROMPTS.map((prompt) => (
                <button
                  key={prompt}
                  type="button"
                  className={styles.samplePromptChip}
                  onClick={() => {
                    setPendingPrompt(prev => ({ text: prompt, key: prev.key + 1 }));
                    setShowAIChat(true);
                  }}
                >
                  <img src="/assets/Solid/Interface/AI.svg" alt="" className={styles.samplePromptIcon} aria-hidden />
                  {prompt}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className={styles.feedbackButtonContainer}>
        <button
          type="button"
          className={styles.feedbackBtn}
          onClick={handleFeedback}
          aria-label={t('feedback', uiLanguage)}
        >
          <img src="/assets/light_bulb.svg" alt="" className={styles.feedbackBtnIcon} />
          <span>{t('feedback', uiLanguage)}</span>
        </button>
      </div>
      <button 
        className={`${styles.saveButtonNew} ${showAIChat ? styles.saveButtonWithChat : ''}`}
        onClick={handleSaveToDashboard}
        disabled={saving}
      >
        <span className={styles.saveButtonLabel}>
          {saving && <span className={styles.saveSpinner} aria-hidden="true" />}
          {saving ? 'SAVING...' : 'SAVE'}
        </span>
        <div className={styles.saveChevron}>
          <div className={styles.saveChevronIcon} />
        </div>
      </button>

      {/* Save Modal */}
      {showSaveModal && (
        <div className={styles.modalOverlay} onClick={handleSaveModalCancel}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>Save Lesson Plan to Dashboard</h3>
            </div>
            <div className={styles.modalBody}>
              <p className={styles.modalDescription}>
                Give your lesson plan a name so you can easily find it later:
              </p>
              <input
                type="text"
                value={tempTitle}
                onChange={(e) => setTempTitle(e.target.value)}
                onKeyDown={handleModalKeyDown}
                className={styles.modalInput}
                placeholder="Enter lesson plan name"
                maxLength={200}
                autoFocus
              />
              <div className={styles.modalInputInfo}>
                {tempTitle.length}/200 characters
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button 
                className={`${styles.modalButton} ${styles.modalCancelButton}`}
                onClick={handleSaveModalCancel}
                disabled={saving}
              >
                Cancel
              </button>
              <button 
                className={`${styles.modalButton} ${styles.modalSaveButton}`}
                onClick={() => handleSaveModalConfirm(false)}
                disabled={saving || !tempTitle.trim()}
              >
                {saving ? 'Saving...' : 'Save to Dashboard'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.successModal}>
            <div className={styles.successIcon}>✓</div>
            <h3 className={styles.successTitle}>Lesson plan saved successfully!</h3>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div className={styles.modalOverlay} onClick={() => setShowErrorModal(false)}>
          <div className={styles.errorModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.errorIcon}>✕</div>
            <h3 className={styles.errorTitle}>{errorMessage}</h3>
            <button 
              className={styles.errorButton}
              onClick={() => setShowErrorModal(false)}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function DisplayLessonPlan() {
  return (
    <ProtectedRoute>
      <DisplayLessonPlanContent />
    </ProtectedRoute>
  );
}

export default DisplayLessonPlan;
